import { Dispatch } from 'redux'
import { mapColorPalette, stateToRegionByName, ZOOM_LEVELS } from '../../../../../constants/Map'
import { AddressActions } from '../../../../../store/Address'
import { AmericanStatesActions } from '../../../../../store/AmericanStates'
import { CountyActions } from '../../../../../store/County'
import { WaterSystemsMetricsActions } from '../../../../../store/publicWaterSystem'
import { WaterSystemGeoBlocksActions } from '../../../../../store/wellWaterSystem'
import {
  ICountyPublicWaterSystemResult,
  IRegionPublicWaterSystemResult,
} from '../../../../../types'
import { getZoomLevels } from './getZoomLevels'
import { getStateCode } from '../../../../../utils/mapHelper'
import {
  fetchAggregatedWaterSystems,
  getCoordinatesFromCounty,
  getStateFromZoom,
} from '../services'
import { removeBoundingBox } from '../styles'
import { removeMarkers } from './handleMapMarkers'

type HandleMoveEndProps = {
  mapRef: React.RefObject<mapboxgl.Map>
  prevSelectedCode: React.MutableRefObject<string | null | undefined>
  prevSelectedCounty: React.MutableRefObject<string | null | undefined>
  markersRef: React.RefObject<mapboxgl.Marker[]>
  dispatch: Dispatch
  countyViolationsRef: React.RefObject<ICountyPublicWaterSystemResult[]>
  regionsViolationsRef: React.RefObject<IRegionPublicWaterSystemResult[]>
  hasDispatchedZoomOut: React.MutableRefObject<boolean>
  selectedAddressRef: React.MutableRefObject<any>
}

export const handleMoveEnd = async ({
  mapRef,
  prevSelectedCounty,
  prevSelectedCode,
  markersRef,
  dispatch,
  countyViolationsRef,
  regionsViolationsRef,
  hasDispatchedZoomOut,
  selectedAddressRef,
}: HandleMoveEndProps) => {
  const map = mapRef.current
  if (!map) return
  if (map.isZooming()) return

  const zoom = map.getZoom()
  const center = map.getCenter()

  if (!zoom || !center) return
  const { isStateZoom, isCountyZoom } = getZoomLevels(zoom)

  // Reset state when zoomed out
  if (zoom < ZOOM_LEVELS.STATE.MIN) {
    setTimeout(() => {
      selectedAddressRef.current = null
    }, 5000)
    if (!hasDispatchedZoomOut.current) {
      handleZoomOut(
        dispatch,
        markersRef,
        prevSelectedCode,
        prevSelectedCounty,
        selectedAddressRef,
        mapRef,
      )
      dispatch(WaterSystemsMetricsActions.fetchWaterSystemMetricsAsync({ options: {} }) as any)
      hasDispatchedZoomOut.current = true
    }
    return
  }

  hasDispatchedZoomOut.current = false

  if (selectedAddressRef.current) {
    const stateCode = getStateCode(selectedAddressRef.current.region)

    const options = {
      params: {
        state_abbr: stateCode,
      },
    }
    dispatch(WaterSystemsMetricsActions.fetchWaterSystemMetricsAsync({ options }) as any)
    //dispatch(AddressActions.setIsAddressClicked(true))
  }

  try {
    // Check if layer exists before querying

    if (!map.getLayer('maine')) return

    const features = map.queryRenderedFeatures(map.project(center), { layers: ['maine'] })

    //TODO: here is where the problem is. on reselect if the map does not come out maine has no features
    if (!features?.length) return
    const feature = features[0]
    if (selectedAddressRef.current) {
      return
    }
    if (isCountyZoom && !isStateZoom) {
      const countyName = feature.properties?.name
      if (countyName && countyName !== prevSelectedCounty.current) {
        getCoordinatesFromCounty(dispatch, countyViolationsRef, countyName, prevSelectedCounty)
      }
      return
    }
    const stateName = feature.properties?.name
    const { code } = stateToRegionByName[stateName] || {}
    if (stateName && code && code !== prevSelectedCode.current) {
      getStateFromZoom(
        dispatch,
        mapRef,
        code,
        prevSelectedCode,
        fetchAggregatedWaterSystems,
        stateName,
      )
    }
  } catch (error) {
    return
  }
}

const handleZoomOut = (
  dispatch: Dispatch,
  markersRef: React.RefObject<mapboxgl.Marker[]>,
  prevSelectedCode: React.MutableRefObject<string | null | undefined>,
  prevSelectedCounty: React.MutableRefObject<string | null | undefined>,
  selectedAddressRef: React.MutableRefObject<any>,
  mapRef: React.RefObject<mapboxgl.Map>,
) => {
  if (!selectedAddressRef?.current) {
    removeMarkers(markersRef)
    removeBoundingBox(mapRef)

    if (mapRef.current) {
      const map = mapRef.current

      if (!map.getLayer('maine')) {
        map.addLayer({
          id: 'maine',
          type: 'fill',
          source: 'maine',
          layout: {},
          paint: {
            'fill-color': [
              'match',
              ['get', 'name'],
              ...Object.entries(stateToRegionByName)
                .map(([stateName, { epaRegion }]) => {
                  const color = mapColorPalette[epaRegion] || '#ffffff'
                  return [stateName, color]
                })
                .flat(),
              '#ffffff',
            ],
            'fill-opacity': 0.5,
          },
        })
      }

      if (!map.getLayer('outline')) {
        map.addLayer({
          id: 'outline',
          type: 'line',
          source: 'maine',
          layout: {},
          paint: {
            'line-color': '#000',
            'line-width': 1,
          },
        })
      }
    }

    dispatch(AddressActions.setSelectedAddress(null))
    dispatch(AddressActions.setIsAddressClicked(false))
  }
  dispatch(CountyActions.setSelectedCounty(''))
  dispatch(WaterSystemGeoBlocksActions.setSelectedGeoBlock(''))
  dispatch(AmericanStatesActions.setSelectedState({}))
  prevSelectedCode.current = null
  prevSelectedCounty.current = null
}
